/* eslint-disable */
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * Extracts method and path from an opblock DOM element.
 */
const extractMethodAndPath = (opblock) => {
  const methodElement = opblock.querySelector('.opblock-summary-method');
  const pathElement = opblock.querySelector('.opblock-summary-path [data-path]') ||
                     opblock.querySelector('.opblock-summary-path a') ||
                     opblock.querySelector('.opblock-summary-path');

  if (!methodElement || !pathElement) return null;

  const method = methodElement.textContent.trim().toUpperCase();
  const path = pathElement.getAttribute('data-path') ||
              pathElement.textContent.trim();

  return { method, path };
};

/**
 * Programmatically sets a <select> element's value and triggers React's change handler.
 */
const setSelectValue = (selectEl, value) => {
  // Check if the value exists as an option
  const hasOption = Array.from(selectEl.options).some(opt => opt.value === value);
  if (!hasOption) {
    console.log(`[MockButtonInjector] Example "${value}" not found in dropdown options`);
    return false;
  }

  // Use the native setter to bypass React's internal tracking
  const nativeSetter = Object.getOwnPropertyDescriptor(
    HTMLSelectElement.prototype, 'value'
  ).set;
  nativeSetter.call(selectEl, value);
  selectEl.dispatchEvent(new Event('change', { bubbles: true }));
  return true;
};

const MockButtonInjector = ({ specSelectors, getSystem, backendSpecsActions, backendSpecsSelectors }) => {
  const lastEnvIdRef = useRef(null);
  const syncInProgressRef = useRef(false);

  const injectButtons = () => {
    // Find all example dropdowns
    const exampleSelects = document.querySelectorAll('.examples-select');

    exampleSelects.forEach(selectContainer => {
      // Check if we already added a button
      if (selectContainer.querySelector('.mock-button-injected')) {
        return;
      }

      // Find the parent operation block
      const opblock = selectContainer.closest('.opblock');
      if (!opblock) return;

      const endpoint = extractMethodAndPath(opblock);
      if (!endpoint) return;
      const { method, path } = endpoint;

      // Create the mock button
      const mockButton = document.createElement('button');
      mockButton.className = 'btn mock-button-injected';
      mockButton.innerHTML = '🎯 Use for Mock';
      mockButton.style.cssText = `
        margin-left: 10px;
        background-color: #4CAF50;
        color: white;
        padding: 3px 10px;
        font-size: 12px;
        font-weight: bold;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        vertical-align: middle;
      `;

      mockButton.onclick = async () => {
        // Get fresh references from the system at click time to avoid stale closures
        const system = getSystem ? getSystem() : null;
        const actions = system?.backendSpecsActions || backendSpecsActions;
        const selectors = system?.backendSpecsSelectors || backendSpecsSelectors;

        console.log('[MockButton] Click - system:', system);
        console.log('[MockButton] Click - actions:', actions);
        console.log('[MockButton] Click - selectors:', selectors);

        // Check if backend-specs plugin is available
        if (!actions || !selectors) {
          console.warn('backend-specs plugin not available');
          alert('Backend connection not available. Please ensure the backend-specs plugin is loaded.');
          return;
        }

        // Check if a spec is loaded - call selector with NO arguments
        const currentSpec = selectors?.selectCurrentSpec?.() || null;
        console.log('[MockButton] currentSpec from selector:', currentSpec);

        if (!currentSpec || !currentSpec.id) {
          console.warn('No current spec found. Current spec:', currentSpec);
          alert('No spec loaded. Please load a spec from the backend first (File > Load from Backend).');
          return;
        }

        // Get the selected example
        const dropdown = selectContainer.querySelector('select');
        const selectedExample = dropdown ? dropdown.value : 'default';

        // Store original button state
        const originalText = mockButton.innerHTML;
        const originalBackground = mockButton.style.backgroundColor;

        // Check if an environment is active
        const activeEnvironment = selectors?.selectActiveEnvironment?.() || null;

        try {
          // Show loading state
          mockButton.disabled = true;
          mockButton.innerHTML = '⏳ Saving...';
          mockButton.style.backgroundColor = '#999';

          let result;
          if (activeEnvironment) {
            console.log(`Saving endpoint setting to environment "${activeEnvironment.name}": ${method} ${path} -> ${selectedExample}`);
            result = await actions.upsertEnvEndpointSetting(activeEnvironment.id, currentSpec.id, path, method, selectedExample, null);
          } else {
            console.log(`Saving endpoint setting (default): ${method} ${path} -> ${selectedExample}`);
            result = await actions.saveEndpointSetting(path, method, selectedExample, null);
          }

          if (result.error) {
            // Show error
            throw new Error(result.meta?.errorMessage || 'Failed to save endpoint setting');
          }

          // Show success state
          const target = activeEnvironment ? ` (${activeEnvironment.name})` : '';
          console.log('Endpoint setting saved successfully:', result.payload);
          mockButton.innerHTML = `✓ Saved${target}`;
          mockButton.style.backgroundColor = '#2e7d32';

          // Revert after 2 seconds
          setTimeout(() => {
            mockButton.innerHTML = originalText;
            mockButton.style.backgroundColor = originalBackground;
            mockButton.disabled = false;
          }, 2000);
        } catch (error) {
          // Show error alert
          console.error('Error saving endpoint setting:', error);
          alert(`Error saving endpoint setting: ${error.message}`);

          // Revert button immediately
          mockButton.innerHTML = originalText;
          mockButton.style.backgroundColor = originalBackground;
          mockButton.disabled = false;
        }
      };

      // Insert the button after the dropdown
      const labelElement = selectContainer.querySelector('label');
      if (labelElement && labelElement.textContent.includes('Examples')) {
        // Insert after the label
        labelElement.parentNode.insertBefore(mockButton, labelElement.nextSibling);
      } else {
        // Append to the container
        selectContainer.appendChild(mockButton);
      }
    });
  };

  /**
   * Syncs example dropdowns in the DOM to match the given endpoint settings.
   * For each setting with a selectedExample, finds the matching <select> by method+path
   * and sets its value.
   */
  const syncExampleDropdowns = (endpointSettings) => {
    if (!endpointSettings || endpointSettings.length === 0) return;

    console.log('[MockButtonInjector] Syncing dropdowns with', endpointSettings.length, 'endpoint settings');

    const exampleSelects = document.querySelectorAll('.examples-select');

    exampleSelects.forEach(selectContainer => {
      const opblock = selectContainer.closest('.opblock');
      if (!opblock) return;

      const endpoint = extractMethodAndPath(opblock);
      if (!endpoint) return;

      const setting = endpointSettings.find(s =>
        s.method?.toUpperCase() === endpoint.method && s.path === endpoint.path
      );

      const dropdown = selectContainer.querySelector('select');
      if (!dropdown) return;

      if (setting?.selectedExample) {
        const changed = setSelectValue(dropdown, setting.selectedExample);
        if (changed) {
          console.log(`[MockButtonInjector] Set ${endpoint.method} ${endpoint.path} -> ${setting.selectedExample}`);
        }
      }
    });
  };

  /**
   * Fetches endpoint settings for the given environment (or default) and syncs dropdowns.
   */
  const fetchAndSyncExamples = async (envId) => {
    if (syncInProgressRef.current) return;
    syncInProgressRef.current = true;

    try {
      const system = getSystem ? getSystem() : null;
      const actions = system?.backendSpecsActions || backendSpecsActions;
      const selectors = system?.backendSpecsSelectors || backendSpecsSelectors;

      const currentSpec = selectors?.selectCurrentSpec?.();
      if (!currentSpec?.id) {
        console.log('[MockButtonInjector] No current spec loaded, skipping sync');
        return;
      }

      let settings = [];

      if (envId) {
        // Fetch environment detail to get endpoint settings
        const result = await actions.fetchEnvironmentDetail(envId);
        if (result?.payload) {
          // Filter to only endpoint settings for the current spec
          settings = (result.payload.endpointSettings || []).filter(
            s => s.specId === currentSpec.id
          );
        }
      } else {
        // Default - use endpoint settings from the loaded spec detail
        const selectedSpec = selectors?.selectSelectedSpec?.();
        if (selectedSpec?.endpointSettings) {
          settings = selectedSpec.endpointSettings;
        }
      }

      console.log('[MockButtonInjector] Environment changed to', envId || 'default',
        '- found', settings.length, 'endpoint settings for spec', currentSpec.id);

      // Small delay to ensure DOM is stable before syncing
      setTimeout(() => syncExampleDropdowns(settings), 300);
    } finally {
      syncInProgressRef.current = false;
    }
  };

  useEffect(() => {
    // Initial injection after a delay
    setTimeout(injectButtons, 1000);

    // Monitor DOM changes
    const observer = new MutationObserver(() => {
      injectButtons();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'aria-expanded']
    });

    // Also monitor clicks to handle expansion
    const clickHandler = () => {
      setTimeout(injectButtons, 500);
    };
    document.addEventListener('click', clickHandler);

    // Poll for active environment changes
    const envCheckInterval = setInterval(() => {
      const system = getSystem ? getSystem() : null;
      const selectors = system?.backendSpecsSelectors || backendSpecsSelectors;
      const activeEnv = selectors?.selectActiveEnvironment?.() || null;
      const currentEnvId = activeEnv?.id || null;

      if (lastEnvIdRef.current !== currentEnvId) {
        console.log('[MockButtonInjector] Active environment changed:',
          lastEnvIdRef.current, '->', currentEnvId);
        lastEnvIdRef.current = currentEnvId;
        fetchAndSyncExamples(currentEnvId);
      }
    }, 500);

    return () => {
      observer.disconnect();
      document.removeEventListener('click', clickHandler);
      clearInterval(envCheckInterval);
    };
  }, []);

  return null; // This component doesn't render anything visible
};

MockButtonInjector.propTypes = {
  specSelectors: PropTypes.object,
  getSystem: PropTypes.func,
  backendSpecsActions: PropTypes.object,
  backendSpecsSelectors: PropTypes.object,
};

MockButtonInjector.defaultProps = {
  specSelectors: null,
  getSystem: null,
  backendSpecsActions: null,
  backendSpecsSelectors: null,
};

export default MockButtonInjector;
